import { oc } from "@orpc/contract";
import { z } from "zod";

const UUIDSchema = z.uuid();

export const TemplateBlockTargetSchema = z.enum([
  "email",
  "pdf",
  "internal_ui",
  "brand-kit",
]);

/**
 * Targets users may create directly. The brand kit is excluded — it is
 * managed through the brand-kit endpoints so its spec is always generated
 * from validated brand tokens.
 */
export const CreatableTemplateBlockTargetSchema = z.enum([
  "email",
  "pdf",
  "internal_ui",
]);
export const TemplateBlockVersionStatusSchema = z.enum(["draft", "finalized"]);

export const TemplateBlockInputSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  type: z.enum(["string", "number", "boolean", "array", "object"]).optional(),
  required: z.boolean().optional(),
  defaultValue: z.unknown().optional(),
  sampleValue: z.unknown().optional(),
});

export const TemplateBlockSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema,
  name: z.string(),
  target: TemplateBlockTargetSchema,
  description: z.string().nullable(),
  tags: z.array(z.string()).nullable(),
  pinnedVersionId: UUIDSchema.nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const TemplateBlockVersionSchema = z.object({
  id: UUIDSchema,
  templateBlockId: UUIDSchema,
  parentVersionId: UUIDSchema.nullable(),
  status: TemplateBlockVersionStatusSchema,
  version: z.number().int(),
  changelog: z.string().nullable(),
  createdBy: UUIDSchema,
  jsonSpec: z.record(z.string(), z.unknown()),
  inputs: z.array(TemplateBlockInputSchema).nullable(),
  sampleState: z.record(z.string(), z.unknown()).nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const ListTemplateBlocksInputSchema = z.object({});
export const ListTemplateBlocksResponseSchema = z.object({
  blocks: z.array(
    TemplateBlockSchema.extend({
      version: TemplateBlockVersionSchema.nullable(),
    }),
  ),
});

export const GetTemplateBlockInputSchema = z.object({ id: UUIDSchema });
export const GetTemplateBlockResponseSchema = z.object({
  block: TemplateBlockSchema,
  version: TemplateBlockVersionSchema.nullable(),
});

export const CreateTemplateBlockInputSchema = z.object({
  name: z.string().min(1),
  target: CreatableTemplateBlockTargetSchema.default("email"),
  description: z.string().nullable().optional(),
  tags: z.array(z.string()).nullable().optional(),
  jsonSpec: z.record(z.string(), z.unknown()),
  inputs: z.array(TemplateBlockInputSchema).nullable().optional(),
  sampleState: z.record(z.string(), z.unknown()).nullable().optional(),
});
export const CreateTemplateBlockResponseSchema = GetTemplateBlockResponseSchema;

export const CreateTemplateBlockDraftInputSchema = z.object({
  templateBlockId: UUIDSchema,
  jsonSpec: z.record(z.string(), z.unknown()).optional(),
  inputs: z.array(TemplateBlockInputSchema).nullable().optional(),
  sampleState: z.record(z.string(), z.unknown()).nullable().optional(),
  changelog: z.string().nullable().optional(),
});
export const CreateTemplateBlockDraftResponseSchema = z.object({
  version: TemplateBlockVersionSchema,
});

export const UpdateTemplateBlockDraftInputSchema = z.object({
  versionId: UUIDSchema,
  jsonSpec: z.record(z.string(), z.unknown()).optional(),
  inputs: z.array(TemplateBlockInputSchema).nullable().optional(),
  sampleState: z.record(z.string(), z.unknown()).nullable().optional(),
  changelog: z.string().nullable().optional(),
});
export const UpdateTemplateBlockDraftResponseSchema =
  CreateTemplateBlockDraftResponseSchema;

export const PublishTemplateBlockInputSchema = z.object({
  templateBlockId: UUIDSchema,
  versionId: UUIDSchema,
});
export const PublishTemplateBlockResponseSchema =
  GetTemplateBlockResponseSchema;

export const DeleteTemplateBlockDraftInputSchema = z.object({
  versionId: UUIDSchema,
});
export const DeleteTemplateBlockDraftResponseSchema = z.object({
  success: z.boolean(),
});

export const ListTemplateBlockVersionsInputSchema = z.object({
  templateBlockId: UUIDSchema,
});
export const ListTemplateBlockVersionsResponseSchema = z.object({
  versions: z.array(TemplateBlockVersionSchema),
});

export const RenderTemplateBlockPreviewInputSchema = z.object({
  jsonSpec: z.record(z.string(), z.unknown()),
  inputs: z.array(TemplateBlockInputSchema).nullable().optional(),
  sampleState: z.record(z.string(), z.unknown()).nullable().optional(),
});
export const RenderTemplateBlockPreviewResponseSchema = z.object({
  html: z.string(),
  plainText: z.string(),
});

export const templateBlocksContracts = {
  list: oc
    .input(ListTemplateBlocksInputSchema)
    .output(ListTemplateBlocksResponseSchema)
    .route({ method: "GET" }),
  get: oc
    .input(GetTemplateBlockInputSchema)
    .output(GetTemplateBlockResponseSchema)
    .route({ method: "GET" }),
  create: oc
    .input(CreateTemplateBlockInputSchema)
    .output(CreateTemplateBlockResponseSchema)
    .route({ method: "POST" }),
  createDraft: oc
    .input(CreateTemplateBlockDraftInputSchema)
    .output(CreateTemplateBlockDraftResponseSchema)
    .route({ method: "POST" }),
  updateDraft: oc
    .input(UpdateTemplateBlockDraftInputSchema)
    .output(UpdateTemplateBlockDraftResponseSchema)
    .route({ method: "POST" }),
  publish: oc
    .input(PublishTemplateBlockInputSchema)
    .output(PublishTemplateBlockResponseSchema)
    .route({ method: "POST" }),
  deleteDraft: oc
    .input(DeleteTemplateBlockDraftInputSchema)
    .output(DeleteTemplateBlockDraftResponseSchema)
    .route({ method: "POST" }),
  listVersions: oc
    .input(ListTemplateBlockVersionsInputSchema)
    .output(ListTemplateBlockVersionsResponseSchema)
    .route({ method: "GET" }),
  renderPreview: oc
    .input(RenderTemplateBlockPreviewInputSchema)
    .output(RenderTemplateBlockPreviewResponseSchema)
    .route({ method: "POST" }),
} as const;

export type TemplateBlockResponse = z.infer<typeof TemplateBlockSchema>;
export type TemplateBlockInput = z.infer<typeof TemplateBlockInputSchema>;
export type TemplateBlockVersionResponse = z.infer<
  typeof TemplateBlockVersionSchema
>;
